/**
 * Auth store (Sprint 8).
 *
 * Зберігає access+refresh+user у localStorage через Zustand persist
 * middleware. Збереження ОБОВ'ЯЗКОВЕ: перезавантаження сторінки браузера
 * не повинно вилогінювати майстра з планшета посеред зміни.
 *
 * Security note: localStorage доступний для XSS. Це свідомий trade-off
 * для PoC — альтернатива (httpOnly cookies) потребує бекенд-змін
 * (SetCookie + CSRF) і відкладена до Sprint 9. JWT-токен НЕ містить
 * sensitive data (тільки claims), тож компроміз керований.
 *
 * Decoded claims тримаємо окремо від raw access_token — щоб роуттер
 * швидко робив role-checks без зайвого jwt-decode на кожен render.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { JwtClaims, LoginUser, TokenPair } from "@/types/api";

/** Парсить claims з access-JWT без верифікації підпису (це робить бекенд).
 *  Тут — лише для UI-логіки (чи протерміновано, які ролі). */
export function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split(".")[1];
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64);
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

export function isExpired(claims: JwtClaims | null, skewSeconds = 30): boolean {
  if (!claims) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp <= nowSec + skewSeconds;
}

/** Рівень доступу до розділу (дзеркало db_access.LEVEL_*). */
export type AccessLevel = "none" | "view" | "full";

/** Рівень із мапи прав — ЛИШЕ через `hasOwnProperty`.
 *
 *  `access["constructor"]` на звичайному об'єкті повертає успадкований
 *  `Object.prototype.constructor` — не `undefined`. Через це розділ
 *  «Конструктор» вважався налаштованим і назавжди лишався без права запису
 *  (те саме чекало б на `toString`, `valueOf`, `hasOwnProperty`). */
export function levelOf(
  access: Record<string, AccessLevel> | undefined,
  section: string,
): AccessLevel | undefined {
  if (!access) return undefined;
  return Object.prototype.hasOwnProperty.call(access, section)
    ? access[section]
    : undefined;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: LoginUser | null;
  claims: JwtClaims | null;
  /** Рівень доступу до розділів (Блок 7, Крок 2), з /auth/me:
   *  none — не видно, view — лише перегляд, full — можна міняти.
   *  Розділу немає в мапі = діє роль із коду. */
  access: Record<string, AccessLevel>;
  /** Позначка середовища («ТЕСТ»). Порожньо на бойовому. */
  envLabel: string | null;

  // Actions
  setSession: (tokens: TokenPair, user: LoginUser) => void;
  /** Викликається з api/client.ts після успішного /auth/refresh. */
  updateTokens: (tokens: TokenPair) => void;
  /** Підтягнути персональні права (після логіну та на старті застосунку). */
  loadAccess: () => Promise<void>;
  /** `leaveDevice` — людина САМА натиснула «Вийти»: відкликати токен цього
   *  пристрою на сервері й забути збережений бейдж. Без нього — тихий вихід
   *  (прострочена сесія): бейдж лишається, щоб власний телефон заходив одним
   *  натиском, як і раніше. */
  logout: (opts?: { leaveDevice?: boolean }) => void;

  // Селектори (тримаємо тут — щоб не дублювати у компонентах)
  hasRole: (role: string) => boolean;
  /** Чи має користувач ХОЧА Б ОДНУ з ролей. Замінює інлайн
   *  `roles.some(r => r === "admin" || ...)`, розсипаний по екранах. */
  hasAnyRole: (...roles: string[]) => boolean;
  /** Чи може людина ЗМІНЮВАТИ щось у розділі. «Перегляд» → false.
   *  Розділ не налаштований → true (вирішує роль, як і раніше). */
  canWrite: (section: string) => boolean;
  /** Чи ПОКАЗУВАТИ розділ або вкладку (`розділ.вкладка`). «none» → false.
   *  Не налаштовано → true (вирішує роль, як і раніше). */
  canSee: (section: string) => boolean;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      claims: null,
      access: {},
      envLabel: null,

      setSession: (tokens, user) => {
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user,
          claims: decodeJwt(tokens.access_token),
        });
        get().loadAccess();
      },

      loadAccess: async () => {
        // Права живуть у БД, а не в токені: адмін змінив — застосувалось без
        // перелогіну. Помилка тут не критична: без винятків діють ролі.
        try {
          const m = await import("@/api/client");
          const me = await m.apiGetRaw<{
            access?: Record<string, AccessLevel>;
            env_label?: string | null;
          }>("/api/v1/auth/me");
          set({ access: me?.access ?? {}, envLabel: me?.env_label ?? null });
        } catch {
          set({ access: {} });
        }
      },

      updateTokens: (tokens) => {
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          claims: decodeJwt(tokens.access_token),
        });
      },

      logout: (opts) => {
        if (opts?.leaveDevice) {
          const refresh = get().refreshToken;
          // Відкликаємо саме цей токен (0124). Не вийшло (немає мережі) — сесію
          // однаково стираємо нижче, а токен помре сам за 7 днів.
          if (refresh) {
            void import("@/api/client").then((m) => m.apiLoginRaw("/api/v1/auth/logout", { refresh_token: refresh }))
              .catch(() => undefined);
          }
          void import("@/lib/deviceBadge").then((m) => m.forgetDeviceBadge());
        }
        // Кеш фото у пам'яті переживає вихід — на спільному планшеті в цеху
        // наступна людина побачила б чужі знімки без жодного запиту на
        // сервер, тобто повз перевірку прав.
        void import("@/components/Photo").then((m) => m.clearPhotoCache());
        // Те саме про офлайн-кеш читань, і він гірший за кеш фото: ключ там —
        // сама адреса, а персональні роути в усіх однакові («мої гроші», «мій
        // цикл»), тож наступна людина побачила б чужі СУМИ. І він переживає
        // перезавантаження, на відміну від кешу фото в памʼяті.
        void import("@/lib/offlineReads").then((m) => m.clearReads());
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          claims: null,
          access: {},
        });
        // Стираємо workspace-стан (інший юзер не побачить попередньої роботи).
        // Динамічний import щоб уникнути циклічної залежності store-ів.
        import("./workspace").then((m) => m.useWorkspaceStore.getState().clearActive());
      },

      hasRole: (role) => {
        const c = get().claims;
        if (!c || !Array.isArray(c.roles)) return false;
        return c.roles.includes(role);
      },

      hasAnyRole: (...roles) => {
        const c = get().claims;
        if (!c || !Array.isArray(c.roles)) return false;
        return roles.some((r) => c.roles!.includes(r));
      },

      // Писати можна лише на «повному» рівні. «Перегляд» і «немає» кнопок
      // збереження не отримують — бек їх однаково не пустить, а людина не
      // має тикати в 403. Розділ без налаштувань (undefined) вирішує роль.
      canWrite: (section) => {
        const level = levelOf(get().access, section);
        return level === undefined || level === "full";
      },

      // Ховати вкладку/розділ можна лише за явним «none»: усе інше (перегляд,
      // повний, «не налаштовано») людина бачить. Бек перевіряє те саме —
      // схований пункт сам по собі не право.
      canSee: (section) => levelOf(get().access, section) !== "none",

      isAuthenticated: () => {
        const { accessToken, claims } = get();
        // Допускаємо протермінований access — refresh-flow у client.ts
        // спробує його оновити. Якщо refresh теж невалідний — client
        // викличе logout().
        return Boolean(accessToken && claims);
      },
    }),
    {
      name: "mes-auth",                       // ключ у localStorage
      storage: createJSONStorage(() => localStorage),
      // Persist ТІЛЬКИ raw-токени і user. claims перерахуємо при rehydration.
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
      }),
      onRehydrateStorage: () => (state) => {
        // Після hydratе — перерахувати claims з access (вони не persist-яться,
        // щоб не плодити двох джерел істини).
        if (state?.accessToken) {
          state.claims = decodeJwt(state.accessToken);
          // І перечитати те, що НЕ зберігається в localStorage: персональні
          // права та позначку середовища. Доти `loadAccess` викликався лише
          // при вході, тож після перезавантаження сторінки смуга «ТЕСТ»
          // зникала — тестова система ставала не відрізнити від бойової.
          // Разом із нею губились і персональні винятки на розділи.
          state.loadAccess();
        }
      },
    },
  ),
);
